<div class="h-full w-full flex flex-col justify-start overflow-hidden space-y-4">
  <app-admin-options-nav />

  <div class="h-full min-h-0 flex-1 flex flex-col w-full overflow-hidden">
    @if (showContextBar()) {
    <section
      class="mb-3 shrink-0 rounded-xl border border-zinc-200 bg-white p-3 shadow-sm dark:border-zinc-800 dark:bg-zinc-950"
      aria-label="Shared admin context"
    >
      <div class="flex flex-wrap items-center justify-between gap-3">
        <h4 class="text-sm font-semibold text-primary">Context</h4>
        <button
          type="button"
          class="inline-flex items-center gap-1.5 rounded-lg border border-zinc-300 bg-white px-2.5 py-1.5 text-xs font-medium text-primary hover:bg-zinc-50 dark:border-zinc-600 dark:bg-zinc-900 dark:hover:bg-zinc-800"
          [attr.aria-expanded]="contextExpanded()"
          aria-controls="admin-settings-context-extras"
          (click)="toggleContextExpanded()"
        >
          @if (contextExpanded()) {
          <span>Show less</span>
          } @else {
          <span>More options</span>
          }
          <svg
            class="h-3.5 w-3.5 shrink-0 transition-transform duration-200"
            [class.rotate-180]="contextExpanded()"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
          >
            <path
              fill-rule="evenodd"
              d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
              clip-rule="evenodd"
            />
          </svg>
        </button>
      </div>

      <div class="mt-3 flex w-full flex-col gap-3 sm:flex-row">
        @if (showBriefIdField()) {
        <div class="min-w-0 flex-1 space-y-1">
          <label for="admin-settings-brief-id" class="block text-sm font-medium text-primary">Brief ID</label>
          <input
            id="admin-settings-brief-id"
            type="text"
            autocomplete="off"
            placeholder="Brief UUID"
            class="block w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-primary shadow-sm dark:border-zinc-700 dark:bg-zinc-950"
            [value]="settingsContext.briefId()"
            (input)="onContextBriefIdInput($any($event.target).value)"
          />
        </div>
        }
        @if (showCreatorIdField()) {
        <div class="min-w-0 flex-1 space-y-1">
          <label for="admin-settings-creator-id" class="block text-sm font-medium text-primary"
            >Creator Firebase UID</label
          >
          <input
            id="admin-settings-creator-id"
            type="text"
            autocomplete="off"
            placeholder="blazeFirebaseUid"
            class="block w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-primary shadow-sm dark:border-zinc-700 dark:bg-zinc-950"
            [value]="settingsContext.creatorId()"
            (input)="onContextCreatorIdInput($any($event.target).value)"
          />
        </div>
        }
      </div>

      <div
        id="admin-settings-context-extras"
        class="admin-context-extras"
        [class.admin-context-extras--open]="contextExpanded()"
        [attr.aria-hidden]="contextExpanded() ? null : true"
      >
        <div class="admin-context-extras__inner">
          <div class="flex flex-wrap items-start justify-between gap-3 pt-3">
            <p class="min-w-0 flex-1 text-xs text-secondary">
              @if (showBriefIdField() && showCreatorIdField()) {
              Brief and creator IDs persist while you move between admin tools that need them.
              } @else if (showCreatorIdField()) {
              Creator ID persists while you move between admin tools that need it.
              } @else {
              Brief ID persists while you move between admin tools that need it.
              }
            </p>
            @if (settingsContext.hasAny() || viewSubmissionUrlInput()) {
            <button
              type="button"
              class="shrink-0 rounded-lg border border-zinc-300 bg-white px-2.5 py-1.5 text-xs font-medium text-primary hover:bg-zinc-50 dark:border-zinc-600 dark:bg-zinc-900 dark:hover:bg-zinc-800"
              (click)="clearContext()"
            >
              Clear IDs
            </button>
            }
          </div>

          <div class="mt-3 space-y-1">
            <label for="admin-settings-view-submission-url" class="block text-sm font-medium text-primary"
              >Paste view-submission URL</label
            >
            <input
              id="admin-settings-view-submission-url"
              type="url"
              autocomplete="off"
              placeholder="https://app.shoutugc.com/view-submission/{briefId}/{creatorId}"
              class="block w-full rounded-lg border border-zinc-200 bg-white px-3 py-2 text-sm text-primary shadow-sm dark:border-zinc-700 dark:bg-zinc-950"
              [class.border-red-400]="viewSubmissionUrlError()"
              [class.dark:border-red-500]="viewSubmissionUrlError()"
              [value]="viewSubmissionUrlInput()"
              (input)="onViewSubmissionUrlInput($any($event.target).value)"
              [attr.inert]="contextExpanded() ? null : ''"
            />
            <p class="text-xs text-secondary">
              Optional shortcut — fills Brief ID and Creator ID from the link path.
            </p>
            @if (viewSubmissionUrlError()) {
            <p class="text-xs text-red-700 dark:text-red-300" role="status">
              Couldn’t read a view-submission link. Expected
              <span class="font-mono">…/view-submission/&#123;briefId&#125;/&#123;creatorId&#125;</span>.
            </p>
            }
          </div>

          @if (contextHint() === 'needs-brief') {
          <p class="mt-2 text-xs text-amber-800 dark:text-amber-200" role="status">
            Creator ID is set. Add a brief ID to use this tool.
          </p>
          } @else if (contextHint() === 'needs-creator') {
          <p class="mt-2 text-xs text-amber-800 dark:text-amber-200" role="status">
            Brief ID is set. Add a creator Firebase UID to use this tool.
          </p>
          }
        </div>
      </div>
    </section>
    }

    <div class="h-full min-h-0 flex flex-row gap-4 w-full">
      <aside
        class="w-60 shrink-0 min-h-0 flex flex-col rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-zinc-800 dark:bg-zinc-950"
        aria-label="Admin automations"
      >
        <div class="shrink-0 border-b border-zinc-200 p-3 dark:border-zinc-800">
          <h4 class="text-sm font-semibold text-primary">Automations</h4>
          <p class="mt-0.5 text-xs text-secondary">Common admin bug-fix tools</p>
        </div>

        <nav class="flex-1 min-h-0 overflow-y-auto p-2 scrollbar-transparent">
          @for (group of toolGroups; track group.category) {
          <div class="mb-3 last:mb-0">
            <p class="px-2 pb-1 text-xs font-medium uppercase tracking-wide text-secondary">
              {{ group.category }}
            </p>
            <ul class="flex flex-col gap-1">
              @for (tool of group.tools; track tool.id) {
              <li>
                <a
                  [routerLink]="getToolLink(tool.id)"
                  class="block w-full rounded-lg border px-3 py-2 text-left transition-colors"
                  [class]="getToolNavClass(tool.id)"
                  [attr.aria-current]="isToolSelected(tool.id) ? 'page' : null"
                >
                  <span class="block text-sm font-medium text-primary">{{ tool.label }}</span>
                  <span class="mt-0.5 block text-xs text-secondary line-clamp-2">{{ tool.description }}</span>
                </a>
              </li>
              }
            </ul>
          </div>
          }
        </nav>
      </aside>

      <section class="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <router-outlet />
      </section>
    </div>
  </div>
</div>
